import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppHeader } from "../AppHeader";

/**
 * Shared layout for static legal pages (Terms, Privacy, Cookies,
 * Accessibility). Mobile-first, matches the Worthify dark landing
 * aesthetic. Renders the global `AppHeader` at the top so every legal
 * page has the same nav (Home, Pricing, Sample Report, etc.) and the
 * page heading sits directly under it.
 */
export const LegalLayout = ({ title, lastUpdated, children, t, language = "en" }) => {
  const navigate = useNavigate();
  const [lang, setLangState] = React.useState(language);
  const setLanguage = (l) => {
    try { localStorage.setItem("worthify_lang", l); } catch { /* ignore */ }
    setLangState(l);
    setTimeout(() => window.location.reload(), 0);
  };
  // For non-EN/PL users we serve the authoritative English text (UK law
  // applies to the Worthify Service) and surface a clear language notice in
  // the user's locale so they understand they're reading the English version.
  // This is best-practice for SaaS (Stripe, Notion, Spotify do the same) —
  // auto-translating legal text risks materially changing legal meaning.
  const needsEnNotice = ["de", "fr", "es"].includes(language);
  return (
    <div
      className="min-h-screen"
      style={{
        background: "var(--wf-app-bg)",
        color: "var(--wf-modal-text-muted)",
      }}
      data-testid="legal-page"
    >
      <AppHeader
        t={t || ((k) => k)}
        language={lang}
        setLanguage={setLanguage}
        onLogoClick={() => navigate("/")}
      />
      <div className="mx-auto" style={{ maxWidth: 760, padding: "104px 18px 64px" }}>
        {/* Header */}
        <div className="mt-2">
          <p
            className="uppercase font-bold"
            style={{ fontSize: 11, letterSpacing: "0.24em", color: "var(--pricing-prem-check)" }}
          >
            Worthify
          </p>
          <h1
            className="font-black mt-2"
            style={{
              fontSize: 32,
              letterSpacing: "-0.6px",
              lineHeight: 1.1,
              color: "var(--wf-modal-text)",
            }}
          >
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-2" style={{ fontSize: 12.5, color: "var(--wf-modal-text-muted)" }}>
              {t ? (t("legalLastUpdated") || "Last updated") : "Last updated"}: {lastUpdated}
            </p>
          )}
        </div>

        {/* Language notice — appears for DE/FR/ES users. The legal text below
            is authoritative English (UK law applies). This banner gives the
            user a localised heads-up. */}
        {needsEnNotice && t && (
          <div
            className="mt-5 flex items-start gap-2.5"
            style={{
              background: "rgba(251,191,36,0.10)",
              border: "1px solid rgba(251,191,36,0.30)",
              borderRadius: 14,
              padding: "12px 14px",
            }}
            data-testid="legal-en-notice"
          >
            <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>🌐</span>
            <p
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                color: "#FEF3C7",
                margin: 0,
              }}
            >
              {t("legalEnNotice")}
            </p>
          </div>
        )}

        {/* Body */}
        <div
          className="mt-6 prose-legal"
          style={{
            background: "var(--wf-modal-card-bg)",
            border: "1px solid rgba(148,163,184,0.18)",
            borderRadius: 18,
            padding: "22px 20px",
            backdropFilter: "blur(8px)",
          }}
        >
          {children}
        </div>

        {/* Inline styles for typography */}
        <style>{`
          .prose-legal h2 {
            font-size: 18px;
            font-weight: 800;
            color: var(--wf-modal-text);
            margin-top: 28px;
            margin-bottom: 8px;
            letter-spacing: -0.2px;
          }
          .prose-legal h2:first-child { margin-top: 0; }
          .prose-legal h3 {
            font-size: 14.5px;
            font-weight: 700;
            color: var(--wf-modal-text);
            margin-top: 18px;
            margin-bottom: 6px;
          }
          .prose-legal p, .prose-legal li {
            font-size: 14px;
            line-height: 1.6;
            color: var(--wf-modal-text-muted);
          }
          .prose-legal p { margin-bottom: 12px; }
          .prose-legal ul {
            list-style-type: disc;
            padding-left: 22px;
            margin-bottom: 14px;
          }
          .prose-legal li { margin-bottom: 6px; }
          .prose-legal strong { color: var(--wf-modal-text); font-weight: 700; }
          .prose-legal a { color: var(--verdict-color-medium); text-decoration: underline; text-underline-offset: 2px; }
          .prose-legal a:hover { color: var(--verdict-color-low); }
          .prose-legal code {
            background: var(--wf-modal-card-bg);
            border: 1px solid rgba(148,163,184,0.25);
            padding: 1px 6px;
            border-radius: 4px;
            font-size: 12.5px;
            color: var(--verdict-color-low);
            font-family: ui-monospace, SFMono-Regular, monospace;
          }
        `}</style>

        {/* Cross-links */}
        <div
          className="mt-6 flex flex-wrap gap-3 justify-center text-center"
          style={{ fontSize: 12 }}
        >
          <Link to="/terms" className="legal-link" style={{ color: "var(--wf-modal-text-muted)" }}>
            {t ? (t("legalNavTerms") || "Terms of Service") : "Terms of Service"}
          </Link>
          <span style={{ color: "var(--wf-modal-text-muted)" }}>·</span>
          <Link to="/privacy" className="legal-link" style={{ color: "var(--wf-modal-text-muted)" }}>
            {t ? (t("legalNavPrivacy") || "Privacy Policy") : "Privacy Policy"}
          </Link>
          <span style={{ color: "var(--wf-modal-text-muted)" }}>·</span>
          <Link to="/cookies" className="legal-link" style={{ color: "var(--wf-modal-text-muted)" }}>
            {t ? (t("legalNavCookies") || "Cookie Policy") : "Cookie Policy"}
          </Link>
          <span style={{ color: "var(--wf-modal-text-muted)" }}>·</span>
          <Link to="/accessibility" className="legal-link" style={{ color: "var(--wf-modal-text-muted)" }}>
            {t ? (t("legalNavAccessibility") || "Accessibility") : "Accessibility"}
          </Link>
        </div>

        <p
          className="mt-4 text-center"
          style={{ fontSize: 11, color: "var(--wf-modal-text-muted)" }}
        >
          © {new Date().getFullYear()} Worthify. {t ? (t("legalAllRights") || "All rights reserved.") : "All rights reserved."}
        </p>
      </div>
    </div>
  );
};
